import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  ComponentSpinner,
  Notification,
  SectionWithTitle,
} from '@app/components';
import { makeRequest } from '@app/utils';
import { sharedMessages } from '@app/translations';

import { DownloadFormat } from '../constants';
import { MarcRecordDto, parseMarcRecord } from './parseMarcRecord';
import { DownloadMarc } from './DowloadMarc';

import './QuickMarcView.scss';

interface QuickMarcViewProps {
  downloadFormats: DownloadFormat[];
}

const cnBem = createBemBlockBuilder(['quickMarcViewTable']);

export const QuickMarcView: FC<QuickMarcViewProps> = ({ downloadFormats }) => {
  const { $t } = useIntl();
  const { id } = useParams();

  const marcFieldsQuery = useQuery(['marcFields', id], () =>
    makeRequest<MarcRecordDto>({
      url: `/opac-inventory/source-records/${id}`,
    }).then(response => parseMarcRecord(response.parsedRecord.content))
  );

  return (
    <div className={cnBem('__wrapper')}>
      {marcFieldsQuery.isLoading && <ComponentSpinner />}
      {marcFieldsQuery.isError && (
        <Notification
          variant="error"
          header={$t({
            id: 'instanceDetails.problemRetrievingMarc',
            defaultMessage: 'There was a problem retrieving the MARC record',
          })}
          description={$t({
            id: 'instanceDetails.pleaseRefreshPage',
            defaultMessage:
              'Please refresh the page or contact your library administrator if the issue persists.',
          })}
        />
      )}
      {marcFieldsQuery.data && (
        <SectionWithTitle
          title={$t(sharedMessages.staffView)}
          titleSibling={<DownloadMarc formats={downloadFormats} />}
        >
          <table aria-label={$t(sharedMessages.staffView)}>
            <tbody>
              {marcFieldsQuery.data.map(([name, value]) => (
                <tr key={`${name}${value}`}>
                  <td>{name}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionWithTitle>
      )}
    </div>
  );
};
